import { useState } from 'react';
import { OnboardingSidePanel } from '../components/OnboardingLayout/OnboardingSidePanel';
import { IdentityVerificationStep } from '../components/IdentityVerificationStep/IdentityVerificationStep';
import { Button } from '@new/common/ui/Button/Button';
import { useNavigate } from '@tanstack/react-router';
import { useCurrentUser } from '@hooks/users';
import { EligibilityCheckStep } from '../components/EligibilityCheckStep/EligibilityCheckStep';
import { VideoVerificationStep } from '../components/VideoVerificationStep/VideoVerificationStep';
import { useUpdateOnboardingMutation } from '@teachur/http-client';

interface OnboardingStepConfig {
  title: string;
  subtitle: React.ReactNode;
  Component: React.ComponentType<any>;
  isValid: (o: Onboarding) => boolean;
  isDeferred?: boolean;
}

const ONBOARDING_STEP_TITLES: OnboardingStepConfig[] = [
  {
    title: 'Eligibility confirmation',
    subtitle: 'Confirm that you are eligible to enroll.',
    Component: EligibilityCheckStep,
    isValid: (o) =>
      o.confirmedAge && o.confirmedSecondarySchoolCompletion && o.confirmedMyAddressIsOutside,
  },
  {
    title: 'Scan and upload your government-issued ID',
    subtitle: (
      <span className="text-[12px] lg:[font-size:var(--fs-p3)]!">
        <strong>Note:</strong> An additional document is optional. All submissions will be verified
        by an enrollment counselor.
      </span>
    ),
    Component: IdentityVerificationStep,
    isValid: (o) => Boolean(o.idImage1 || o.idImage2),
  },
  {
    title: 'Watch a short orientation video',
    subtitle: 'Get an overview of the enrollment process and what comes next. (8 minute video)',
    Component: VideoVerificationStep,
    isValid: (o) => o.watchedVideo,
    isDeferred: true,
  },
] as const;

export const OnboardingPage = () => {
  const [step, setStep] = useState(0);
  const [watchedVideo, setWatchedVideo] = useState(false);
  const navigate = useNavigate();

  const { title, subtitle, Component, isValid, isDeferred } = ONBOARDING_STEP_TITLES[step];

  const isLastStep = step === ONBOARDING_STEP_TITLES.length - 1;

  const user = useCurrentUser();
  const { mutate: updateOnboarding } = useUpdateOnboardingMutation();

  const onboarding = user.data?.onboarding;
  const isStepCompleted = isDeferred ? watchedVideo : onboarding ? isValid(onboarding) : false;

  const onSubmit = () => {
    if (isLastStep) {
      updateOnboarding({ watchedVideo: true }, { onSuccess: () => navigate({ to: '/' }) });
    } else {
      setStep((prev) => prev + 1);
    }
  };

  return (
    <div className="flex flex-col lg:grid lg:grid-cols-[auto_1fr] w-full h-svh lg:h-screen p-5 box-border bg-[#FAFDFF]">
      <OnboardingSidePanel activeStep={step} onStepChange={setStep} />
      <div className="grid w-full flex-1">
        <div className="grid place-items-center">
          <div className="w-[min(100%,720px)] flex flex-col gap-8 lg:gap-10 items-center text-center">
            <div className="flex flex-col items-center gap-1">
              <h1 className="whitespace-pre-line hidden md:block [font-family:var(--font-arvo)]! [font-size:var(--fs-h4)]! lg:[font-size:var(--fs-h1)]! font-bold! m-0! text-(--neutral-black-NB1000)! leading-tight">
                {title}
              </h1>
              <p className="[font-family:var(--font-montserrat)]! [font-size:var(--fs-p4)]! m-0! leading-tight text-(--neutral-black-NB900)! [&_strong]:font-semibold">
                {subtitle}
              </p>
            </div>
            <div className="w-[min(100%,656px)]">
              {isDeferred ? (
                <VideoVerificationStep value={watchedVideo} onChange={setWatchedVideo} />
              ) : (
                <Component />
              )}
            </div>
          </div>
        </div>
        <div className="grid place-items-center pb-2">
          <Button
            variant="primary"
            className="w-[min(100%,720px)]"
            onClick={onSubmit}
            disabled={!isStepCompleted}
          >
            {isLastStep ? 'Finish Onboarding' : 'Next'}
          </Button>
        </div>
      </div>
    </div>
  );
};
